
import { LoginForm } from "@/components/auth/LoginForm";
import { Footer } from "@/components/footer";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { devLog } from "@/utils/devLog";
import { useAuth } from "@/providers/AuthProvider";
import { Loader2 } from "lucide-react";
import { getPendingEnrollment } from "@/utils/pendingEnrollment";

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [redirectParams, setRedirectParams] = useState<URLSearchParams | null>(null);
  const { session, initialSessionProcessed, loading } = useAuth();
  
  const from = location.state?.from as string | undefined;
  
  const urlParams = new URLSearchParams(window.location.search);
  
  const courseToEnroll = searchParams.get("course") || urlParams.get("course");
  const price = searchParams.get("price") || urlParams.get("price");
  const name = searchParams.get("name") || urlParams.get("name");
  const isEnrollFlow = (searchParams.get("enroll") || urlParams.get("enroll")) === "true";
  
  devLog("[Login] Component loaded with complete URL:", window.location.href);
  devLog("[Login] Search params object:", Object.fromEntries(searchParams.entries()));
  devLog("[Login] Direct URL params:", Object.fromEntries(urlParams.entries()));
  devLog("[Login] Is enrollment flow:", isEnrollFlow);
  devLog("[Login] Course parameter from URL:", courseToEnroll);
  devLog("[Login] From state:", from);
  
  useEffect(() => {
    if (!isEnrollFlow) {
      sessionStorage.removeItem('pendingEnrollment');
      devLog('[Login] Cleared pendingEnrollment - no enroll flag present');
    }
  }, [isEnrollFlow]);
  
  useEffect(() => {
    if (isEnrollFlow && courseToEnroll) {
      sessionStorage.setItem('pendingEnrollment', JSON.stringify({
        courseId: courseToEnroll,
        price,
        name,
        from,
        timestamp: new Date().toISOString()
      }));
      devLog("[Login] Stored enrollment data in sessionStorage");
    }
  }, [courseToEnroll, price, name, isEnrollFlow, from]);
  
  // Check for pending enrollment on session change
  useEffect(() => {
    if (initialSessionProcessed && session) {
      devLog("[Login] User authenticated, checking for pending enrollment");
      
      // Check if there's a pending enrollment from localStorage
      const pendingEnrollment = getPendingEnrollment();
      
      if (pendingEnrollment && pendingEnrollment.courseId) {
        devLog("[Login] Found pending enrollment, redirecting to dashboard for processing:", pendingEnrollment);
        // Go directly to dashboard, where the usePendingEnrollmentRedirect hook will process it
        navigate("/dashboard", { replace: true });
        return;
      }
      
      if (from) {
        devLog("[Login] Redirecting to original location:", from);
        navigate(from);
      } else {
        devLog("[Login] No pending enrollment or specific redirect target, going to dashboard");
        navigate("/dashboard", { replace: true });
      }
    }
  }, [initialSessionProcessed, session, navigate, from]);
  
  useEffect(() => {
    if (isEnrollFlow && courseToEnroll) {
      const params = new URLSearchParams();
      params.append('course', courseToEnroll);
      params.append('enroll', 'true');
      
      if (price) params.append('price', price);
      if (name) params.append('name', name);
      
      setRedirectParams(params);
      
      const redirectUrl = `/student-auth?${params.toString()}`;
      
      devLog("[Login] Course enrollment detected, redirecting to student-auth");
      devLog("[Login] Course ID:", courseToEnroll);
      devLog("[Login] Complete redirect params:", Object.fromEntries(params.entries()));
      devLog("[Login] Redirect URL:", redirectUrl);
      
      navigate(redirectUrl, { state: { from } });
    }
  }, [courseToEnroll, price, name, navigate, isEnrollFlow, from]);
  
  if (loading || !initialSessionProcessed) {
    return (
      <div className="min-h-screen flex flex-col">
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-lg">Checking your session...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 flex items-center justify-center py-12 px-4 bg-primary-50">
        <LoginForm redirectParams={redirectParams} from={from} />
      </main>
      <Footer />
    </div>
  );
};

export default Login;
